import { CompassusNavigatorServiceImplFactory } from "../../core/impl/compassusNavigatorServiceImpl";

export const CompassusNavigatorControllerFactory = ({
  compassusNavigatorService,
}: {
  compassusNavigatorService: ReturnType<
    typeof CompassusNavigatorServiceImplFactory
  >;
}) => {
  const getPatients = async ({
    clinicId,
    queryParams,
  }: {
    clinicId: number;
    queryParams?: ICompassusNavigatorQueryParams;
  }) => {
    try {
      const result = await compassusNavigatorService.getPatients({
        clinicId,
        queryParams,
      });
      if (result.success && result.data) {
        return {
          success: true as const,
          data: result.data,
        };
      }
      return {
        success: false as const,
        error: result.error,
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  /**
   * Fetches just the patient ids matching the current filters (ids_only=true
   * on the navigator endpoint). Cheap enough that callers can skip page_size
   * entirely and get every match. See CompassusNavigator hook's
   * fetchAllMatchingPatientIds for usage.
   */
  const getPatientIds = async ({
    clinicId,
    queryParams,
  }: {
    clinicId: number;
    queryParams?: Omit<ICompassusNavigatorQueryParams, "ids_only">;
  }) => {
    try {
      const result = await compassusNavigatorService.getPatientIds({
        clinicId,
        queryParams,
      });
      if (result.success && result.data) {
        return {
          success: true as const,
          data: result.data,
        };
      }
      return {
        success: false as const,
        error: result.error,
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getCompletedVisits = async ({
    clinicId,
    queryParams,
  }: {
    clinicId: number;
    queryParams?: ICompassusCompletedVisitsQueryParams;
  }) => {
    try {
      const result = await compassusNavigatorService.getCompletedVisits({
        clinicId,
        queryParams,
      });
      if (result.success && result.data) {
        return {
          success: true as const,
          data: result.data,
        };
      }
      return {
        success: false as const,
        error: result.error,
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const exportNavigator = async ({
    clinicId,
    body,
  }: {
    clinicId: number;
    body: ICompassusNavigatorExportRequest;
  }) => {
    try {
      const result = await compassusNavigatorService.exportNavigator({
        clinicId,
        body,
      });
      if (result.success && result.data) {
        return {
          success: true as const,
          data: result.data,
        };
      }
      return {
        success: false as const,
        error: result.error,
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getCounts = async ({
    clinicId,
    queryParams,
  }: {
    clinicId: number;
    queryParams?: CompassusNavigatorCountsQueryParams;
  }) => {
    try {
      const result = await compassusNavigatorService.getCounts({
        clinicId,
        queryParams,
      });
      if (result.success && result.data) {
        return {
          success: true as const,
          data: result.data,
        };
      }
      return {
        success: false as const,
        error: result.error,
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPayerFilters = async ({ clinicId }: { clinicId: number }) => {
    try {
      const result = await compassusNavigatorService.getPayerFilters({
        clinicId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getMedicationFilters = async ({ clinicId }: { clinicId: number }) => {
    try {
      const result = await compassusNavigatorService.getMedicationFilters({
        clinicId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getAllergyFilters = async ({ clinicId }: { clinicId: number }) => {
    try {
      const result = await compassusNavigatorService.getAllergyFilters({
        clinicId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getConditionFilters = async ({ clinicId }: { clinicId: number }) => {
    try {
      const result = await compassusNavigatorService.getConditionFilters({
        clinicId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getLastVisitProviderFilters = async ({
    clinicId,
    queryParams,
  }: {
    clinicId: number;
    queryParams?: Pick<ICompassusNavigatorQueryParams, "circadia">;
  }) => {
    try {
      const result =
        await compassusNavigatorService.getLastVisitProviderFilters({
          clinicId,
          queryParams,
        });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getNextVisitProviderFilters = async ({
    clinicId,
    queryParams,
  }: {
    clinicId: number;
    queryParams?: Pick<ICompassusNavigatorQueryParams, "circadia">;
  }) => {
    try {
      const result =
        await compassusNavigatorService.getNextVisitProviderFilters({
          clinicId,
          queryParams,
        });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getTodoTagFilters = async ({ clinicId }: { clinicId: number }) => {
    try {
      const result = await compassusNavigatorService.getTodoTagFilters({
        clinicId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getCustomTagFilters = async ({ clinicId }: { clinicId: number }) => {
    try {
      const result = await compassusNavigatorService.getCustomTagFilters({
        clinicId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getClinicalRiskScoreFilters = async ({
    clinicId,
  }: {
    clinicId: number;
  }) => {
    try {
      const result =
        await compassusNavigatorService.getClinicalRiskScoreFilters({
          clinicId,
        });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const patchPatientTodo = async ({
    clinicId,
    patientId,
    todoTags,
  }: {
    clinicId: number;
    patientId: number;
    todoTags: string[];
  }) => {
    try {
      const result = await compassusNavigatorService.patchPatientTodo({
        clinicId,
        patientId,
        todoTags,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const patchPatientHhJourneyStatus = async ({
    clinicId,
    patientId,
    body,
  }: {
    clinicId: number;
    patientId: number;
    body: ICompassusHhJourneyStatusPatchRequest;
  }) => {
    try {
      const result =
        await compassusNavigatorService.patchPatientHhJourneyStatus({
          clinicId,
          patientId,
          body,
        });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPatientHhJourneyStatus = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: number;
  }) => {
    try {
      const result = await compassusNavigatorService.getPatientHhJourneyStatus({
        clinicId,
        patientId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getHhCadence = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: number;
  }) => {
    try {
      return await compassusNavigatorService.getHhCadence({
        clinicId,
        patientId,
      });
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const patchHhCadence = async ({
    clinicId,
    patientId,
    body,
  }: {
    clinicId: number;
    patientId: number;
    body: ICompassusHhCadencePatchRequest;
  }) => {
    try {
      return await compassusNavigatorService.patchHhCadence({
        clinicId,
        patientId,
        body,
      });
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPatientDataReview = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: number;
  }) => {
    try {
      const result = await compassusNavigatorService.getPatientDataReview({
        clinicId,
        patientId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPatientInsurance = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: number;
  }) => {
    try {
      const result = await compassusNavigatorService.getPatientInsurance({
        clinicId,
        patientId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPatientInsuranceRawResponse = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: number;
  }) => {
    try {
      const result =
        await compassusNavigatorService.getPatientInsuranceRawResponse({
          clinicId,
          patientId,
        });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getHhEligibilityWorklist = async ({
    clinicId,
    params,
  }: {
    clinicId: number;
    params: ICompassusHhEligibilityWorklistQueryParams;
  }) => {
    try {
      const result = await compassusNavigatorService.getHhEligibilityWorklist({
        clinicId,
        params,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getHhNpWorklist = async ({
    clinicId,
    params,
  }: {
    clinicId: number;
    params: ICompassusHhNpWorklistQueryParams;
  }) => {
    try {
      const result = await compassusNavigatorService.getHhNpWorklist({
        clinicId,
        params,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getHhTechnicianWorklist = async ({
    clinicId,
    params,
  }: {
    clinicId: number;
    params: ICompassusHhTechnicianWorklistQueryParams;
  }) => {
    try {
      const result = await compassusNavigatorService.getHhTechnicianWorklist({
        clinicId,
        params,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getHhGlobalAdminWorklist = async ({
    clinicId,
    queryParams,
  }: {
    clinicId: number;
    queryParams: ICompassusHhGlobalAdminWorklistQueryParams;
  }) => {
    try {
      const result = await compassusNavigatorService.getHhGlobalAdminWorklist({
        clinicId,
        queryParams,
      });
      if (result.success && result.data) {
        return { success: true as const, data: result.data };
      }
      return { success: false as const, error: result.error };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getHhGlobalAdminWorklistCounts = async ({
    clinicId,
    body,
  }: {
    clinicId: number;
    body: ICompassusHhGlobalAdminCountsRequest;
  }) => {
    try {
      const result =
        await compassusNavigatorService.getHhGlobalAdminWorklistCounts({
          clinicId,
          body,
        });
      if (result.success && result.data) {
        return { success: true as const, data: result.data };
      }
      return { success: false as const, error: result.error };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const patchHhNpUnlinkDraft = async ({
    clinicId,
    assignmentId,
    ipvNoteId,
  }: {
    clinicId: number;
    assignmentId: number;
    ipvNoteId: number;
  }) => {
    try {
      const result = await compassusNavigatorService.patchHhNpUnlinkDraft({
        clinicId,
        assignmentId,
        ipvNoteId,
      });
      if (result.success) return { success: true as const, data: result.data };
      return { success: false as const, error: result.error };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const rescheduleHhVisitAssignment = async ({
    clinicId,
    patientId,
    assignmentId,
    body,
  }: {
    clinicId: number;
    patientId: number;
    assignmentId: number;
    body: ICompassusHhVisitScheduleRequest;
  }) => {
    try {
      const result =
        await compassusNavigatorService.rescheduleHhVisitAssignment({
          clinicId,
          patientId,
          assignmentId,
          body,
        });
      if (result.success) return { success: true as const, data: result.data };
      return { success: false as const, error: result.error };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const cancelHhVisitAssignment: ICancelCompassusHhVisitAssignment = async ({
    clinicId,
    patientId,
    assignmentId,
  }) => {
    try {
      return await compassusNavigatorService.cancelHhVisitAssignment({
        clinicId,
        patientId,
        assignmentId,
      });
    } catch (error) {
      return {
        success: false,
        error: { message: (error as Error).message },
      };
    }
  };

  const createHhFollowUp = async ({
    clinicId,
    patientId,
    body,
  }: {
    clinicId: number;
    patientId: number;
    body: ICompassusHhFollowUpRequest;
  }) => {
    try {
      return await compassusNavigatorService.createHhFollowUp({
        clinicId,
        patientId,
        body,
      });
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const createHhAtRiskFollowUp = async ({
    clinicId,
    patientId,
    body,
  }: {
    clinicId: number;
    patientId: number;
    body: ICompassusHhAtRiskFollowUpRequest;
  }) => {
    try {
      const result = await compassusNavigatorService.createHhAtRiskFollowUp({
        clinicId,
        patientId,
        body,
      });
      if (result.success && result.data) {
        return { success: true as const, data: result.data };
      }
      return {
        success: false as const,
        error: !result.success ? result.error : { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const patchPatientCustomTags = async ({
    clinicId,
    patientId,
    tags,
  }: {
    clinicId: number;
    patientId: number;
    tags: string[];
  }) => {
    try {
      const result = await compassusNavigatorService.patchPatientCustomTags({
        clinicId,
        patientId,
        tags,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPatientCustomTags = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: number;
  }) => {
    try {
      const result = await compassusNavigatorService.getPatientCustomTags({
        clinicId,
        patientId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getHospitalizationRecords = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: number;
  }) =>
    compassusNavigatorService.getHospitalizationRecords({
      clinicId,
      patientId,
    });

  const createHospitalizationRecord = async ({
    clinicId,
    patientId,
    body,
  }: {
    clinicId: number;
    patientId: number;
    body: ICompassusHospitalizationCreateRequest;
  }) =>
    compassusNavigatorService.createHospitalizationRecord({
      clinicId,
      patientId,
      body,
    });

  const updateHospitalizationRecord = async ({
    clinicId,
    patientId,
    recordId,
    body,
  }: {
    clinicId: number;
    patientId: number;
    recordId: number;
    body: ICompassusHospitalizationPatchRequest;
  }) =>
    compassusNavigatorService.updateHospitalizationRecord({
      clinicId,
      patientId,
      recordId,
      body,
    });

  const deleteHospitalizationRecord = async ({
    clinicId,
    patientId,
    recordId,
  }: {
    clinicId: number;
    patientId: number;
    recordId: number;
  }) =>
    compassusNavigatorService.deleteHospitalizationRecord({
      clinicId,
      patientId,
      recordId,
    });

  const getPatientAssociations = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: number;
  }) => {
    try {
      const result = await compassusNavigatorService.getPatientAssociations({
        clinicId,
        patientId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  /**
   * Bulk to-do mutation. Swap in for N parallel `patchPatientTodo` calls.
   * See IBulkPatchCompassusPatientTodo for mode semantics.
   */
  const bulkPatchPatientTodo = async ({
    clinicId,
    patientIds,
    mode,
    todoTags,
  }: {
    clinicId: number;
    patientIds: number[];
    mode: CompassusBulkTodoMode;
    todoTags?: string[];
  }) => {
    try {
      const result = await compassusNavigatorService.bulkPatchPatientTodo({
        clinicId,
        patientIds,
        mode,
        todoTags,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const putWatchlist = async ({
    clinicId,
    items,
  }: {
    clinicId: number;
    items: ICompassusWatchlistItem[];
  }) => {
    try {
      const result = await compassusNavigatorService.putWatchlist({
        clinicId,
        items,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      return {
        success: false as const,
        error: !result.success ? result.error : undefined,
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const patchWatchlist = async ({
    clinicId,
    add,
    remove,
  }: {
    clinicId: number;
    add: number[];
    remove: number[];
  }) => {
    try {
      const result = await compassusNavigatorService.patchWatchlist({
        clinicId,
        add,
        remove,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      return {
        success: false as const,
        error: !result.success ? result.error : undefined,
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getAcuityLevelFilters = async ({ clinicId }: { clinicId: number }) => {
    try {
      const result = await compassusNavigatorService.getAcuityLevelFilters({
        clinicId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  /**
   * Per-patient trend endpoints. Items are newest-first, one entry per
   * source visit, no roll-up. Safe to plot directly. Prefer reading
   * `latest_*` fields off `ICompassusPatient` when the list payload is
   * available; reach for these only when drawing a trend or detail view
   * that needs the full history.
   */
  const getPatientAcuityLevelHistory = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: number;
  }) => {
    try {
      const result =
        await compassusNavigatorService.getPatientAcuityLevelHistory({
          clinicId,
          patientId,
        });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPatientPrognosisHistory = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: number;
  }) => {
    try {
      const result = await compassusNavigatorService.getPatientPrognosisHistory(
        {
          clinicId,
          patientId,
        }
      );
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPatientFormHistory = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: number;
  }) => {
    try {
      const result = await compassusNavigatorService.getPatientFormHistory({
        clinicId,
        patientId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  /**
   * Single-patient hospice eligibility / risk score lookup. Same score +
   * detail fields surfaced inline on each navigator list row, plus the
   * deterministic rubric and LLM-derived fields used by the Hospice Risk
   * tab on the patient profile. Returns a 200 with null score fields when
   * the patient belongs to the clinic but has not yet been scored.
   */
  const getPatientHospiceEligibilityScore = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: number;
  }) => {
    try {
      const result =
        await compassusNavigatorService.getPatientHospiceEligibilityScore({
          clinicId,
          patientId,
        });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  /**
   * Append-only history of hospice eligibility scores for a single
   * patient. Ordered oldest → newest, ready to plot as a trend line.
   * The backend records a new snapshot whenever a scoring input
   * changes, so two adjacent points can carry the same score with
   * different `scored_at` stamps. `includeRubric` inflates each row
   * with its full input snapshot for drilldown; omit by default to
   * keep the payload small for the trend view.
   *
   * Unlike `getPatientHospiceEligibilityScore` (clinic-scoped path),
   * this endpoint is patient-scoped only — the backend enforces
   * clinic access against the clinic that owns the patient, so no
   * `clinicId` is forwarded.
   */
  const getPatientHospiceEligibilityScoreHistory = async ({
    patientId,
    includeRubric,
    limit,
  }: {
    patientId: number;
    includeRubric?: boolean;
    limit?: number;
  }) => {
    try {
      const result =
        await compassusNavigatorService.getPatientHospiceEligibilityScoreHistory(
          {
            patientId,
            includeRubric,
            limit,
          }
        );
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  /**
   * Forced LLM refresh for a single patient's hospice eligibility score.
   * Re-runs the deterministic rubric step (keeping the base score +
   * history current) then forces an LLM refresh, returning the refreshed
   * scores so the caller can render immediately. Patient-scoped only —
   * the backend enforces clinic access against the clinic that owns the
   * patient, so no `clinicId` is forwarded.
   *
   * `data.status` distinguishes outcomes the UI should react to:
   * `no_base_score` (never rule-scored → show a hint), `success`,
   * `partial_failure`, and `failed`.
   */
  const refreshPatientHospiceEligibilityScoreLlm = async ({
    patientId,
  }: {
    patientId: number;
  }) => {
    try {
      const result =
        await compassusNavigatorService.refreshPatientHospiceEligibilityScoreLlm(
          {
            patientId,
          }
        );
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPatientHospiceEligibilityScoreLlmRefreshStatus = async ({
    patientId,
  }: {
    patientId: number;
  }) => {
    try {
      const result =
        await compassusNavigatorService.getPatientHospiceEligibilityScoreLlmRefreshStatus(
          {
            patientId,
          }
        );
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  /**
   * Structural schema for every Compassus assessment form. Pure projection
   * of the pipeline templates — body is identical per clinic and only
   * changes on deploy, so the FE should cache with a singleton SWR key
   * and `useSWRImmutable` (see `FormTrendCard`).
   */
  const getFormsSchema = async ({ clinicId }: { clinicId: number }) => {
    try {
      const result = await compassusNavigatorService.getFormsSchema({
        clinicId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPatientCommunicationNumbers = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: number;
  }) => {
    try {
      const result =
        await compassusNavigatorService.getPatientCommunicationNumbers({
          clinicId,
          patientId,
        });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const linkPatientCommunicationNumber = async ({
    clinicId,
    patientId,
    body,
  }: {
    clinicId: number;
    patientId: number;
    body: ICompassusPatientCommunicationNumberRequest;
  }) => {
    try {
      const result =
        await compassusNavigatorService.linkPatientCommunicationNumber({
          clinicId,
          patientId,
          body,
        });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const setPrimaryPatientCommunicationNumber = async ({
    clinicId,
    patientId,
    phoneNumber,
  }: {
    clinicId: number;
    patientId: number;
    phoneNumber: string;
  }) => {
    try {
      const result =
        await compassusNavigatorService.setPrimaryPatientCommunicationNumber({
          clinicId,
          patientId,
          phoneNumber,
        });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const updatePatientCommunicationNumberLabel = async ({
    clinicId,
    patientId,
    linkId,
    label,
  }: {
    clinicId: number;
    patientId: number;
    linkId: number;
    label: string;
  }) => {
    try {
      return await compassusNavigatorService.updatePatientCommunicationNumberLabel(
        { clinicId, patientId, linkId, label }
      );
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const removePatientCommunicationNumber = async ({
    clinicId,
    patientId,
    linkId,
  }: {
    clinicId: number;
    patientId: number;
    linkId: number;
  }) => {
    try {
      return await compassusNavigatorService.removePatientCommunicationNumber({
        clinicId,
        patientId,
        linkId,
      });
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPatientCommunicationSms = async ({
    clinicId,
    patientId,
    phoneNumber,
    limit,
  }: {
    clinicId: number;
    patientId: number;
    phoneNumber: string;
    limit?: number;
  }) => {
    try {
      const result = await compassusNavigatorService.getPatientCommunicationSms(
        {
          clinicId,
          patientId,
          phoneNumber,
          limit,
        }
      );
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPatientCommunicationSmsThreads = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: number;
  }) => {
    try {
      const result =
        await compassusNavigatorService.getPatientCommunicationSmsThreads({
          clinicId,
          patientId,
        });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const sendPatientCommunicationSms = async ({
    clinicId,
    patientId,
    body,
  }: {
    clinicId: number;
    patientId: number;
    body: ICompassusPatientSmsSendRequest;
  }) => {
    try {
      const result =
        await compassusNavigatorService.sendPatientCommunicationSms({
          clinicId,
          patientId,
          body,
        });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const sendNpAppointmentSms = async ({
    clinicId,
    patientId,
    body,
  }: {
    clinicId: number;
    patientId: number;
    body: ICompassusNpAppointmentSmsRequest;
  }) => {
    try {
      const result = await compassusNavigatorService.sendNpAppointmentSms({
        clinicId,
        patientId,
        body,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPatientCommunicationCalls = async ({
    clinicId,
    patientId,
    limit,
  }: {
    clinicId: number;
    patientId: number;
    limit?: number;
  }) => {
    try {
      const result =
        await compassusNavigatorService.getPatientCommunicationCalls({
          clinicId,
          patientId,
          limit,
        });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPatientCommunicationCallToken = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: number;
  }) => {
    try {
      const result =
        await compassusNavigatorService.getPatientCommunicationCallToken({
          clinicId,
          patientId,
        });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const startPatientCommunicationCall = async ({
    clinicId,
    patientId,
    body,
  }: {
    clinicId: number;
    patientId: number;
    body: ICompassusPatientStartCallRequest;
  }) => {
    try {
      const result =
        await compassusNavigatorService.startPatientCommunicationCall({
          clinicId,
          patientId,
          body,
        });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getHhRpmCommunicationNotificationCount: IGetHhRpmCommunicationNotificationCount =
    (payload) =>
      compassusNavigatorService.getHhRpmCommunicationNotificationCount(payload);

  const getHhRpmCommunicationThreads: IGetHhRpmCommunicationThreads = (
    payload
  ) => compassusNavigatorService.getHhRpmCommunicationThreads(payload);

  const getHhRpmNotificationEpisodes: IGetHhRpmNotificationEpisodes = (
    payload
  ) => compassusNavigatorService.getHhRpmNotificationEpisodes(payload);

  const reviewHhRpmCommunicationNotification: IReviewHhRpmCommunicationNotification =
    (payload) =>
      compassusNavigatorService.reviewHhRpmCommunicationNotification(payload);

  const getHhRpmSchedulerAlertRecipients: IGetHhRpmSchedulerAlertRecipients = (
    payload
  ) => compassusNavigatorService.getHhRpmSchedulerAlertRecipients(payload);

  const createHhRpmSchedulerAlertRecipient: ICreateHhRpmSchedulerAlertRecipient =
    (payload) =>
      compassusNavigatorService.createHhRpmSchedulerAlertRecipient(payload);

  const removeHhRpmSchedulerAlertRecipient: IRemoveHhRpmSchedulerAlertRecipient =
    (payload) =>
      compassusNavigatorService.removeHhRpmSchedulerAlertRecipient(payload);

  return {
    getPatients,
    getPatientIds,
    getCompletedVisits,
    exportNavigator,
    getCounts,
    getPayerFilters,
    getMedicationFilters,
    getAllergyFilters,
    getConditionFilters,
    getLastVisitProviderFilters,
    getNextVisitProviderFilters,
    getTodoTagFilters,
    getCustomTagFilters,
    getClinicalRiskScoreFilters,
    patchPatientTodo,
    getPatientHhJourneyStatus,
    patchPatientHhJourneyStatus,
    getHhCadence,
    patchHhCadence,
    getPatientDataReview,
    getPatientInsurance,
    getPatientInsuranceRawResponse,
    getHhEligibilityWorklist,
    getHhNpWorklist,
    getHhTechnicianWorklist,
    getHhGlobalAdminWorklist,
    getHhGlobalAdminWorklistCounts,
    patchHhNpUnlinkDraft,
    rescheduleHhVisitAssignment,
    cancelHhVisitAssignment,
    createHhFollowUp,
    createHhAtRiskFollowUp,
    patchPatientCustomTags,
    getPatientCustomTags,
    getHospitalizationRecords,
    createHospitalizationRecord,
    updateHospitalizationRecord,
    deleteHospitalizationRecord,
    getPatientAssociations,
    bulkPatchPatientTodo,
    putWatchlist,
    patchWatchlist,
    getAcuityLevelFilters,
    getPatientAcuityLevelHistory,
    getPatientPrognosisHistory,
    getPatientFormHistory,
    getPatientHospiceEligibilityScore,
    getPatientHospiceEligibilityScoreHistory,
    getPatientHospiceEligibilityScoreLlmRefreshStatus,
    refreshPatientHospiceEligibilityScoreLlm,
    getFormsSchema,
    getPatientCommunicationNumbers,
    linkPatientCommunicationNumber,
    setPrimaryPatientCommunicationNumber,
    updatePatientCommunicationNumberLabel,
    removePatientCommunicationNumber,
    getPatientCommunicationSms,
    getPatientCommunicationSmsThreads,
    sendPatientCommunicationSms,
    sendNpAppointmentSms,
    getPatientCommunicationCalls,
    getPatientCommunicationCallToken,
    startPatientCommunicationCall,
    getHhRpmCommunicationNotificationCount,
    getHhRpmCommunicationThreads,
    getHhRpmNotificationEpisodes,
    reviewHhRpmCommunicationNotification,
    getHhRpmSchedulerAlertRecipients,
    createHhRpmSchedulerAlertRecipient,
    removeHhRpmSchedulerAlertRecipient,
  };
};
